import { 
    createSlice, 
    createEntityAdapter, 
    EntityState, 
    PayloadAction, 
    createAsyncThunk,
    ActionCreatorWithPayload
} from '@reduxjs/toolkit';
import type { RootState, AppDispatch } from '../index';
import { collection, onSnapshot, QuerySnapshot, DocumentData, Timestamp, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db as firestore } from '@/lib/firebase';
// Adjust import path as needed
import type { Brand, Drink, Flavor, Attribute, DrinkFlavor, DrinkAttribute } from '@/types/firestore'; 

// --- Helper Function to Serialize Timestamps ---
// Recursively searches for Firestore Timestamps and converts them to ISO strings
const serializeTimestamps = (data: any): any => {
    if (data === null || typeof data !== 'object') {
        return data;
    }

    if (data instanceof Timestamp) {
        return data.toDate().toISOString(); // Convert Timestamp to ISO string
    }

    if (Array.isArray(data)) {
        return data.map(serializeTimestamps);
    }

    // For plain objects, process each value
    const serializedObject: { [key: string]: any } = {};
    for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
            serializedObject[key] = serializeTimestamps(data[key]);
        }
    }
    return serializedObject;
};

// Define entity adapters
const brandsAdapter = createEntityAdapter<Brand>();
const drinksAdapter = createEntityAdapter<Drink>();
const flavorsAdapter = createEntityAdapter<Flavor>();
const attributesAdapter = createEntityAdapter<Attribute>();

// Define the state shape
interface GlobalDataState {
  brands: EntityState<Brand, string>;
  drinks: EntityState<Drink, string>;
  flavors: EntityState<Flavor, string>;
  attributes: EntityState<Attribute, string>;
  loading: 'idle' | 'pending' | 'succeeded' | 'failed'; // General loading for initializeGlobalData
  error: string | null; // General error for initializeGlobalData
  initialized: boolean; // To track if initial fetch/listener setup is done
  fetchDrinkByIdStatus: 'idle' | 'pending' | 'succeeded' | 'failed'; // Specific status for fetchDrinkById
  fetchDrinkByIdError: string | null; // Specific error for fetchDrinkById
}

// Define the initial state using adapters
const initialState: GlobalDataState = {
  brands: brandsAdapter.getInitialState(),
  drinks: drinksAdapter.getInitialState(),
  flavors: flavorsAdapter.getInitialState(),
  attributes: attributesAdapter.getInitialState(),
  loading: 'idle',
  error: null,
  initialized: false,
  fetchDrinkByIdStatus: 'idle',
  fetchDrinkByIdError: null,
};

// Define the slice first to get access to action creators
const globalDataSlice = createSlice({
    name: 'globalData',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<'idle' | 'pending' | 'succeeded' | 'failed'>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
            state.loading = 'failed'; // Set loading to failed on error
        },
        setInitialized: (state, action: PayloadAction<boolean>) => {
            state.initialized = action.payload;
        },
        // Reducers using adapter methods
        setBrands: (state, action: PayloadAction<Brand[]>) => {
            brandsAdapter.setAll(state.brands, action.payload);
        },
        setDrinks: (state, action: PayloadAction<Drink[]>) => {
            drinksAdapter.setAll(state.drinks, action.payload);
        },
        setFlavors: (state, action: PayloadAction<Flavor[]>) => {
            flavorsAdapter.setAll(state.flavors, action.payload);
        },
        setAttributes: (state, action: PayloadAction<Attribute[]>) => {
            attributesAdapter.setAll(state.attributes, action.payload);
        },
        resetGlobalData: (state) => {
            Object.assign(state, initialState); // Reset all fields to initial state
            // Crucially, also reset the module-level listenersInitialized flag
            listenersInitialized = false;
            // And clear out any active listener subscriptions
            listenerUnsubscribes.forEach(unsub => unsub());
            listenerUnsubscribes = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(initializeGlobalData.pending, (state) => {
                state.loading = 'pending'; 
                state.error = null;
                // Do not set initialized to false here, as a re-attempt might occur
                // while data is partially there from a previous successful but interrupted session.
            })
            .addCase(initializeGlobalData.rejected, (state, action) => {
                state.initialized = false; // Mark as not initialized on failure
                listenersInitialized = false; // Reset listener flag
                state.loading = 'failed';
                state.error = action.payload ?? 'Initialization failed';
            })
            .addCase(initializeGlobalData.fulfilled, (state) => {
                state.loading = 'succeeded'; 
                state.initialized = true; 
                state.error = null; // Clear any previous error on successful init
            })
            .addCase(updateDrinkCommunityEdits.pending, () => {
                // No specific loading state for this, assumes quick or handled by global listeners
            })
            .addCase(updateDrinkCommunityEdits.fulfilled, (state, action) => {
                console.log(state);
                console.log('Drink community edits updated successfully for:', action.meta.arg.drinkId);
                // Data update is handled by the listener for the 'drinks' collection via setDrinks
            })
            .addCase(updateDrinkCommunityEdits.rejected, (state, action) => {
                console.log(state);
                console.error('Failed to update drink community edits:', action.payload);
                // Optionally set a specific error state for this operation if needed
            })
            // Cases for fetchDrinkById
            .addCase(fetchDrinkById.pending, (state) => {
                state.fetchDrinkByIdStatus = 'pending';
                state.fetchDrinkByIdError = null;
            })
            .addCase(fetchDrinkById.fulfilled, (state, action) => {
                state.fetchDrinkByIdStatus = 'succeeded';
                if (action.payload) { // action.payload is Drink | null
                    drinksAdapter.upsertOne(state.drinks, action.payload);
                }
            })
            .addCase(fetchDrinkById.rejected, (state, action) => {
                state.fetchDrinkByIdStatus = 'failed';
                state.fetchDrinkByIdError = action.payload ?? 'Failed to fetch drink by ID';
            });
    },
});

// Export actions AFTER slice definition
export const {
    setLoading,
    setError,
    setInitialized,
    setBrands,
    setDrinks,
    setFlavors,
    setAttributes,
    resetGlobalData,
} = globalDataSlice.actions;

// Type helper for listener setup using ActionCreatorWithPayload
interface ListenerConfig<T> {
    name: string; // Firestore collection name
    setAction: ActionCreatorWithPayload<T[]>; // Use the specific action creator type
}

// Keep track of listener unsubscribes globally within this module
let listenerUnsubscribes: (() => void)[] = [];
let listenersInitialized = false;

// Async thunk to initialize Firestore listeners
export const initializeGlobalData = createAsyncThunk<
    void,
    void,
    { dispatch: AppDispatch; rejectValue: string; state: RootState }
>(
    'globalData/initialize',
    async (_, { dispatch, rejectWithValue, getState }) => {
        console.log('[GlobalData THUNK] Attempting initialization...');
        const currentState = getState().globalData;

        // Strict idempotency checks for an already successful and complete initialization
        if (currentState.initialized && currentState.loading === 'succeeded' && listenersInitialized) {
            console.log('[GlobalData THUNK] Skipping: Already successfully initialized and listeners confirmed active.');
            return;
        }

        // If another instance of this thunk is already in 'pending' state 
        // AND has already set the listenersInitialized flag (meaning it's the one setting up listeners),
        // this current instance should back off.
        if (currentState.loading === 'pending' && listenersInitialized) {
             console.log('[GlobalData THUNK] Skipping: Another instance appears to be setting up listeners (state is pending and listenersInitialized is true).');
             return;
        }
        
        // If global state is initialized but module-level listenersInitialized is false
        // (e.g. after resetGlobalData or a very specific error recovery path), reset listenersInitialized to allow re-setup.
        if (currentState.initialized && !listenersInitialized) {
            console.warn('[GlobalData THUNK] Slice state is initialized but listenersInitialized flag is false. Forcing listener re-setup.');
            // No need to set listenersInitialized = false here, as the next block will set it true.
        }
        
        console.log('[GlobalData THUNK] Proceeding with actual listener setup logic.');
        
        // Clean up any existing listeners FOR THIS THUNK INSTANCE before starting new ones.
        // This specific `listenerUnsubscribes` array is module-level, so this ensures only one set of listeners is active.
        console.log('[GlobalData THUNK] Cleaning up any existing listeners before new setup...');
        listenerUnsubscribes.forEach(unsub => unsub());
        listenerUnsubscribes = [];
        // This instance is now taking responsibility for setting up listeners.
        listenersInitialized = true; 

        const collectionsToListen: (
            ListenerConfig<Brand> |
            ListenerConfig<Drink> |
            ListenerConfig<Flavor> |
            ListenerConfig<Attribute>
        )[] = [
            { name: 'brands', setAction: setBrands },
            { name: 'drinks', setAction: setDrinks }, 
            { name: 'flavors', setAction: setFlavors },
            { name: 'attributes', setAction: setAttributes },
        ];

        const listenerPromises = collectionsToListen.map(({ name, setAction }) => {
            return new Promise<void>((resolve, reject) => {
                console.log(`[GlobalData THUNK] Setting up listener for: ${name}`);
                const q = collection(firestore, name);
                const unsubscribe = onSnapshot(q,
                    (snapshot: QuerySnapshot<DocumentData>) => {
                        console.log(`[GlobalData THUNK] Data received for: ${name}, docs count: ${snapshot.docs.length}`);
                        const data = snapshot.docs
                            .map(doc => {
                                const docData = doc.data();
                                if (!docData) return null;
                                const serializedData = serializeTimestamps(docData); 
                                return { id: doc.id, ...serializedData };
                            })
                            .filter(item => item !== null); 
                        dispatch(setAction(data as any)); 
                        console.log(`[GlobalData THUNK] Dispatched setAction for: ${name}`);
                        resolve();
                    },
                    (error) => {
                        console.error(`[GlobalData THUNK] Error in onSnapshot for ${name}:`, error);
                        reject(new Error(`Failed to load ${name}: ${error.message}`)); 
                    }
                );
                listenerUnsubscribes.push(unsubscribe);
            });
        });

        try {
            console.log('[GlobalData THUNK] Awaiting all listener promises...');
            await Promise.all(listenerPromises);
            console.log('[GlobalData THUNK] All listener promises resolved. Initialization considered successful by this thunk instance.');
            // The .fulfilled action will set slice state.initialized = true and loading = 'succeeded'
        } catch (error: any) {
            console.error("[GlobalData THUNK] Critical error during Promise.all(listenerPromises) for listener setup:", error);
            listenerUnsubscribes.forEach(unsub => unsub()); 
            listenerUnsubscribes = [];
            listenersInitialized = false; // IMPORTANT: Reset flag as listener setup failed.
            return rejectWithValue(error.message || 'Initialization failed critically during listener setup');
        }
    }
);

// Function to cleanup listeners
export const cleanupGlobalDataListeners = () => {
    console.log('[GlobalData] Cleaning up listeners...');
    listenerUnsubscribes.forEach(unsub => unsub());
    listenerUnsubscribes = [];
    listenersInitialized = false;
    // Note: This does not dispatch resetGlobalData. That should be a conscious decision by the caller.
};

interface UpdateDrinkCommunityEditsPayload {
  drinkId: string;
  flavors: DrinkFlavor[]; 
  attributes: DrinkAttribute[]; 
}

export const updateDrinkCommunityEdits = createAsyncThunk<
  void, 
  UpdateDrinkCommunityEditsPayload,
  { state: RootState; rejectValue: string }
>(
  'globalData/updateDrinkCommunityEdits',
  async ({ drinkId, flavors, attributes }, { rejectWithValue }) => {
    try {
      const drinkRef = doc(firestore, 'drinks', drinkId);
      await updateDoc(drinkRef, {
        flavors: flavors,
        attributes: attributes,
      });
      // The listener on the 'drinks' collection will pick this up and update the store via setDrinks.
    } catch (error: any) {
      console.error("Error updating drink community edits:", error);
      return rejectWithValue(error.message || 'Failed to update drink community edits');
    }
  }
);

// New thunk to fetch a specific drink by ID
export const fetchDrinkById = createAsyncThunk<
  Drink | null,
  string,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'globalData/fetchDrinkById',
  async (drinkId, { getState, rejectWithValue }) => {
    try {
      const existingDrink = selectDrinkById(getState(), drinkId);
      if (existingDrink) {
        console.log(`[GlobalData] Drink ${drinkId} already in store (via selectDrinkById).`);
        return existingDrink;
      }

      console.log(`[GlobalData] Fetching drink ${drinkId} from Firestore for specific request...`);
      const drinkRef = doc(firestore, 'drinks', drinkId);
      const drinkSnap = await getDoc(drinkRef);
      
      if (!drinkSnap.exists()) {
        console.log(`[GlobalData] Drink ${drinkId} not found in Firestore during specific fetch.`);
        return null;
      }

      const drinkData = drinkSnap.data();
      const serializedData = serializeTimestamps(drinkData);
      const drinkToUpsert = { id: drinkSnap.id, ...serializedData } as Drink;
      
      // The fulfilled extraReducer for fetchDrinkById will handle upserting this.
      console.log(`[GlobalData] Successfully fetched drink ${drinkId} for specific request: ${drinkToUpsert.name}`);
      return drinkToUpsert;
    } catch (error: any) {
      console.error(`[GlobalData] Error in fetchDrinkById for ${drinkId}:`, error);
      return rejectWithValue(error.message || `Failed to fetch drink ${drinkId}`);
    }
  }
);

// Export reducer AFTER slice and actions
export default globalDataSlice.reducer;

// --- Selectors ---

// Base selectors
export const selectGlobalDataState = (state: RootState) => state.globalData;
export const selectGlobalDataLoading = (state: RootState) => state.globalData.loading;
export const selectGlobalDataError = (state: RootState) => state.globalData.error;
export const selectGlobalDataInitialized = (state: RootState) => state.globalData.initialized;
export const selectFetchDrinkByIdStatus = (state: RootState) => state.globalData.fetchDrinkByIdStatus;
export const selectFetchDrinkByIdError = (state: RootState) => state.globalData.fetchDrinkByIdError;

// Entity-specific selectors using adapter's getSelectors
export const selectBrandEntities = (state: RootState) => state.globalData.brands;
export const selectDrinkEntities = (state: RootState) => state.globalData.drinks;
export const selectFlavorEntities = (state: RootState) => state.globalData.flavors;
export const selectAttributeEntities = (state: RootState) => state.globalData.attributes;

// Memoized selectors for arrays of entities
export const {
    selectAll: selectAllBrands,
    selectById: selectBrandById,
    selectIds: selectBrandIds
} = brandsAdapter.getSelectors(selectBrandEntities);

export const {
    selectAll: selectAllDrinks,
    selectById: selectDrinkById,
    selectIds: selectDrinkIds
} = drinksAdapter.getSelectors(selectDrinkEntities);

export const {
    selectAll: selectAllFlavors,
    selectById: selectFlavorById,
    selectIds: selectFlavorIds
} = flavorsAdapter.getSelectors(selectFlavorEntities);

export const {
    selectAll: selectAllAttributes,
    selectById: selectAttributeById,
    selectIds: selectAttributeIds
} = attributesAdapter.getSelectors(selectAttributeEntities);

// Modified selector function: no longer dispatches initializeGlobalData itself.
export const selectOrFetchDrinkById = (drinkId: string) => 
  async (dispatch: AppDispatch, getState: () => RootState): Promise<Drink | null> => {
    const storeState = getState();
    const drink = selectDrinkById(storeState, drinkId);
    if (drink) {
      console.log(`[selectOrFetch] Drink ${drinkId} found in store.`);
      return drink;
    }

    const isGloballyInitialized = selectGlobalDataInitialized(storeState);
    const globalLoadingStatus = selectGlobalDataLoading(storeState);

    // If global data isn't initialized yet, component should wait or show loading.
    // This thunk will not trigger global initialization.
    if (!isGloballyInitialized) {
      if (globalLoadingStatus === 'pending') {
        console.log(`[selectOrFetch] Global data initialization is pending for drink ${drinkId}. Component should wait.`);
      } else {
        console.warn(`[selectOrFetch] Global data not initialized for drink ${drinkId}. App.tsx should handle initialization.`);
      }
      return null; 
    }

    // Global data is initialized, but the drink is not in the store. Fetch it specifically.
    const specificFetchStatus = selectFetchDrinkByIdStatus(storeState);
    if (specificFetchStatus !== 'pending') {
        console.log(`[selectOrFetch] Drink ${drinkId} not in store, global data initialized. Dispatching fetchDrinkById.`);
        const fetchAction = await dispatch(fetchDrinkById(drinkId));
        if (fetchDrinkById.fulfilled.match(fetchAction)) {
            return fetchAction.payload; // This will be the drink or null
        }
        // If rejected, the component using this thunk should observe selectFetchDrinkByIdError.
        console.error(`[selectOrFetch] fetchDrinkById for ${drinkId} was rejected or did not return a drink.`);
        return null; 
    } else {
        console.log(`[selectOrFetch] Specific fetch for drink ${drinkId} is already pending. Component should wait.`);
        return null; // A fetch is already in progress for this drink.
    }
  }; 